// A visitor's privacy choices: what they are and how they are kept. Before
// anyone has chosen, nothing optional runs and they are asked, wherever they
// are: counsel's Cookie Notice (October 2026) promises that no cookie other
// than a necessary one is set until the visitor chooses through the banner.
//
// The record lives in localStorage, which the site may use without asking:
// remembering the choice is what makes it a choice. It is versioned and dated,
// so a change to the purposes asks everyone again, and a choice ages out.

import { safeStorage } from './safeStorage';

export const CONSENT_STORAGE_KEY = 'wc.consent';

/**
 * Bump when the purposes, or the words that describe them, change. Every
 * choice made under an older version is then treated as no choice.
 */
export const CONSENT_VERSION = 1;

/** How long a choice is kept before the visitor is asked again. */
export const CONSENT_MAX_AGE_DAYS = 180;

/** The optional purposes. What is strictly necessary is never asked about. */
export interface ConsentChoices {
  /** Google Analytics and Metricool. */
  analytics: boolean;
  /** Advertising cookies used to measure whether an ad led to an inquiry. */
  advertising: boolean;
}

/** Which button the choice came from, kept for the record. */
export type ConsentMethod = 'accept_all' | 'reject_optional' | 'custom';

/**
 * The rule a choice was made under. Every choice is `opt_in` now. `standard`
 * is only on choices made while visitors outside Europe had analytics on by
 * default (until October 2026), and those are asked again: see `readConsent`.
 */
export type ConsentRegion = 'opt_in' | 'standard';

export interface ConsentRecord extends ConsentChoices {
  version: number;
  method: ConsentMethod;
  /** The rule in force when they chose. */
  region: ConsentRegion;
  decided_at: string;
  expires_at: string;
}

export const NO_OPTIONAL: ConsentChoices = { analytics: false, advertising: false };
export const ALL_OPTIONAL: ConsentChoices = { analytics: true, advertising: true };

/**
 * Whether this browser sends Global Privacy Control, the "do not sell or share
 * my information" signal California and several other states require a site
 * to honor.
 */
export function browserSendsGpc(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/**
 * What a choice comes to in this browser. Global Privacy Control keeps
 * advertising measurement off whatever was chosen, since that is the purpose
 * that shares data with an ad platform. Analytics stays the visitor's call.
 */
export function honorGpc(choices: ConsentChoices, gpc: boolean): ConsentChoices {
  return gpc && choices.advertising ? { ...choices, advertising: false } : choices;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function isRecord(value: unknown): value is ConsentRecord {
  if (!value || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.version === 'number' &&
    typeof r.analytics === 'boolean' &&
    typeof r.advertising === 'boolean' &&
    (r.method === 'accept_all' || r.method === 'reject_optional' || r.method === 'custom') &&
    (r.region === 'opt_in' || r.region === 'standard') &&
    typeof r.decided_at === 'string' &&
    typeof r.expires_at === 'string' &&
    !Number.isNaN(Date.parse(r.decided_at)) &&
    !Number.isNaN(Date.parse(r.expires_at))
  );
}

/**
 * The stored choice, if it still stands. One from an older version, one past
 * its expiry, or anything unreadable is removed and reads as no choice. So is
 * one made under the old standard rule: the settings dialog opened with
 * analytics already ticked for those visitors, so saving it was not clearly a
 * choice to turn it on.
 */
export function readConsent(now: Date = new Date()): ConsentRecord | null {
  const raw = safeStorage.getItem(CONSENT_STORAGE_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (
    !isRecord(parsed) ||
    parsed.version !== CONSENT_VERSION ||
    parsed.region !== 'opt_in' ||
    Date.parse(parsed.expires_at) <= now.getTime()
  ) {
    safeStorage.removeItem(CONSENT_STORAGE_KEY);
    return null;
  }
  return parsed;
}

/**
 * Keep a choice. Returns the record either way: a browser that refuses storage
 * still gets its choice for this visit, and is asked again on the next.
 */
export function saveConsent(
  choices: ConsentChoices,
  method: ConsentMethod,
  now: Date = new Date(),
): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    analytics: choices.analytics,
    advertising: choices.advertising,
    method,
    region: 'opt_in',
    decided_at: now.toISOString(),
    expires_at: new Date(now.getTime() + CONSENT_MAX_AGE_DAYS * DAY_MS).toISOString(),
  };
  safeStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  return record;
}

export type GoogleConsentValue = 'granted' | 'denied';

export interface GoogleConsentState {
  analytics_storage: GoogleConsentValue;
  ad_storage: GoogleConsentValue;
  ad_user_data: GoogleConsentValue;
  ad_personalization: GoogleConsentValue;
}

/**
 * The choices in Google's consent mode terms. Advertising measurement grants
 * ad storage and ad user data together. Ad personalization is never granted:
 * nothing here is used to choose which ads someone sees.
 */
export function googleConsentState(choices: ConsentChoices): GoogleConsentState {
  const value = (on: boolean): GoogleConsentValue => (on ? 'granted' : 'denied');
  return {
    analytics_storage: value(choices.analytics),
    ad_storage: value(choices.advertising),
    ad_user_data: value(choices.advertising),
    ad_personalization: 'denied',
  };
}
